// @ts-nocheck
/**
 * Contact Sales / Demo Request Page
 * Part of Educational Value Proposition flow using Carbon Design System
 * Navigation Flow: Landing → Value Content → ROI Calculator → Success Stories → Contact Sales
 */

import React, { useState } from 'react';
import {
  Grid,
  Column,
  Form,
  FluidTextInput,
  FluidTextArea,
  FluidSelect,
  SelectItem,
  Button,
  ButtonSet,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  Tile,
  Tag,
  ProgressIndicator,
  ProgressStep,
  InlineNotification,
  Modal,
  DatePicker,
  DatePickerInput,
  TimePicker,
  TimePickerSelect,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
  Breadcrumb,
  BreadcrumbItem,
  Loading,
  Layer,
} from '@carbon/react';
import {
  ArrowRight,
  Calendar,
  Phone,
  Email,
  Location,
  CheckmarkFilled,
  UserMultiple,
  Education,
  Calculator,
  Document,
  Time,
} from '@carbon/icons-react';
import { MarketingVariant } from '../../routes/MarketingRouter';
import MarketingHeader from '../../components/marketing/MarketingHeader';
import MarketingFooter from '../../components/marketing/MarketingFooter';
import { useNavigate } from 'react-router-dom';

interface DemoRequestPageProps {
  variant?: MarketingVariant;
}

interface DemoFormData {
  name: string;
  email: string;
  phone: string;
  organization: string;
  role: string;
  organizationType: string;
  studentCount: string;
  timeline: string;
  challenges: string[];
  interests: string[];
  preferredDate: string;
  preferredTime: string;
  message: string;
  consent: boolean;
}

const DemoRequestPage: React.FC<DemoRequestPageProps> = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DemoFormData>({
    name: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    organizationType: '',
    studentCount: '',
    timeline: '',
    challenges: [],
    interests: [],
    preferredDate: '',
    preferredTime: '',
    message: '',
    consent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const organizationTypes = [
    { value: '', text: 'Select organization type' },
    { value: 'k12-school', text: 'K-12 School' },
    { value: 'school-district', text: 'School District' },
    { value: 'higher-ed', text: 'Higher Education' },
    { value: 'tutoring-center', text: 'Tutoring Center' },
    { value: 'homeschool', text: 'Homeschool Network' },
    { value: 'corporate-training', text: 'Corporate Training' },
    { value: 'other', text: 'Other' },
  ];

  const roles = [
    { value: '', text: 'Select your role' },
    { value: 'superintendent', text: 'Superintendent' },
    { value: 'principal', text: 'Principal' },
    { value: 'teacher', text: 'Teacher' },
    { value: 'it-director', text: 'IT Director' },
    { value: 'curriculum-director', text: 'Curriculum Director' },
    { value: 'parent', text: 'Parent' },
    { value: 'administrator', text: 'Administrator' },
    { value: 'other', text: 'Other' },
  ];

  const challenges = [
    'Low student engagement',
    'Teacher workload',
    'Parent involvement',
    'Academic performance',
    'Resource allocation',
    'Data analytics',
    'Personalized learning',
    'Administrative efficiency',
  ];

  const interests = [
    'AI Tutoring',
    'Progress Analytics',
    'Parent Portal',
    'Teacher Tools',
    'Curriculum Alignment',
    'Assessment Tools',
    'Professional Development',
    'District Management',
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.organization) newErrors.organization = 'Organization is required';
    if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.organizationType) newErrors.organizationType = 'Organization type is required';
    if (!formData.consent) newErrors.consent = 'Please agree to be contacted';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // In production, this would call the actual API
      const response = await fetch('/api/demo-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: 'value-proposition-flow',
          variant,
        }),
      });

      setShowSuccess(true);
    } catch (error) {
      console.error('Error submitting demo request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof DemoFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCheckboxChange = (field: 'challenges' | 'interests', value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked ? [...prev[field], value] : prev[field].filter(item => item !== value),
    }));
  };

  return (
    <div className="demo-request-page">
      <MarketingHeader variant={variant} />

      {/* Breadcrumb Navigation */}
      <Layer>
        <Grid>
          <Column lg={16} md={8} sm={4} style={{ paddingTop: 'var(--cds-spacing-05)' }}>
            <Breadcrumb noTrailingSlash aria-label="Page navigation">
              <BreadcrumbItem href="/">Home</BreadcrumbItem>
              <BreadcrumbItem href="/demo" isCurrentPage>
                Request Demo
              </BreadcrumbItem>
            </Breadcrumb>
          </Column>
        </Grid>
      </Layer>

      {/* Progress Indicator */}
      <Layer style={{ backgroundColor: 'var(--cds-background-hover)' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ padding: 'var(--cds-spacing-06) 0' }}>
              <ProgressIndicator currentIndex={4} spaceEqually>
                <ProgressStep label="Discover Platform" />
                <ProgressStep label="Explore Value" />
                <ProgressStep label="Calculate ROI" />
                <ProgressStep label="Success Stories" />
                <ProgressStep label="Get Started" />
              </ProgressIndicator>
            </div>
          </Column>
        </Grid>
      </Layer>

      <Grid>
        <Column lg={12} md={6} sm={4}>
          <div style={{ padding: '4rem 0' }}>
            <h1
              style={{
                fontSize: 'var(--cds-heading-05-font-size)',
                fontWeight: 'var(--cds-heading-05-font-weight)',
                marginBottom: 'var(--cds-spacing-05)',
                color: 'var(--cds-interactive-01)',
              }}
            >
              Schedule Your Personalized Demo
            </h1>
            <p
              style={{
                fontSize: 'var(--cds-body-long-02-font-size)',
                lineHeight: 'var(--cds-body-long-02-line-height)',
                marginBottom: 'var(--cds-spacing-06)',
                color: 'var(--cds-text-secondary)',
              }}
            >
              See how Sigma08 can transform your educational outcomes. Our education specialists will provide a
              customized demonstration based on your specific needs and goals.
            </p>

            <Form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
                <h3
                  style={{
                    marginBottom: 'var(--cds-spacing-05)',
                    fontSize: 'var(--cds-heading-03-font-size)',
                    fontWeight: 'var(--cds-heading-03-font-weight)',
                  }}
                >
                  Contact Information
                </h3>
                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <FluidTextInput
                      id="name"
                      labelText="Full Name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={e => handleInputChange('name', e.target.value)}
                      invalid={!!errors.name}
                      invalidText={errors.name}
                      required
                    />
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <FluidTextInput
                      id="email"
                      labelText="Email Address"
                      placeholder="john.smith@school.edu"
                      type="email"
                      value={formData.email}
                      onChange={e => handleInputChange('email', e.target.value)}
                      invalid={!!errors.email}
                      invalidText={errors.email}
                      required
                    />
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <FluidTextInput
                      id="phone"
                      labelText="Phone Number (Optional)"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={e => handleInputChange('phone', e.target.value)}
                      helperText="For urgent demo scheduling questions"
                    />
                  </Column>
                </Grid>
              </div>

              <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
                <h3
                  style={{
                    marginBottom: 'var(--cds-spacing-05)',
                    fontSize: 'var(--cds-heading-03-font-size)',
                    fontWeight: 'var(--cds-heading-03-font-weight)',
                  }}
                >
                  Organization Details
                </h3>
                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <FluidTextInput
                      id="organization"
                      labelText="Organization Name"
                      placeholder="Lincoln School District"
                      value={formData.organization}
                      onChange={e => handleInputChange('organization', e.target.value)}
                      invalid={!!errors.organization}
                      invalidText={errors.organization}
                      required
                    />
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <FluidSelect
                      id="role"
                      labelText="Your Role"
                      value={formData.role}
                      onChange={e => handleInputChange('role', e.target.value)}
                      invalid={!!errors.role}
                      invalidText={errors.role}
                    >
                      {roles.map(role => (
                        <SelectItem key={role.value} value={role.value} text={role.text} />
                      ))}
                    </FluidSelect>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <FluidSelect
                      id="organizationType"
                      labelText="Organization Type"
                      value={formData.organizationType}
                      onChange={e => handleInputChange('organizationType', e.target.value)}
                      invalid={!!errors.organizationType}
                      invalidText={errors.organizationType}
                    >
                      {organizationTypes.map(type => (
                        <SelectItem key={type.value} value={type.value} text={type.text} />
                      ))}
                    </FluidSelect>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <RadioButtonGroup
                      legendText="Number of Students"
                      name="studentCount"
                      value={formData.studentCount}
                      onChange={value => handleInputChange('studentCount', value)}
                    >
                      <RadioButton labelText="1-100" value="1-100" />
                      <RadioButton labelText="101-500" value="101-500" />
                      <RadioButton labelText="501-1000" value="501-1000" />
                      <RadioButton labelText="1000+" value="1000+" />
                    </RadioButtonGroup>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <RadioButtonGroup
                      legendText="Implementation Timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={value => handleInputChange('timeline', value)}
                    >
                      <RadioButton labelText="Immediate" value="immediate" />
                      <RadioButton labelText="This Quarter" value="quarter" />
                      <RadioButton labelText="This Year" value="year" />
                      <RadioButton labelText="Exploring Options" value="exploring" />
                    </RadioButtonGroup>
                  </Column>
                </Grid>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Your Educational Needs</h3>
                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <fieldset>
                      <legend style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                        Current Challenges (Select all that apply)
                      </legend>
                      {challenges.map(challenge => (
                        <Checkbox
                          key={challenge}
                          id={`challenge-${challenge}`}
                          labelText={challenge}
                          checked={formData.challenges.includes(challenge)}
                          onChange={(_, { checked }) => handleCheckboxChange('challenges', challenge, checked)}
                        />
                      ))}
                    </fieldset>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <fieldset>
                      <legend style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                        Areas of Interest (Select all that apply)
                      </legend>
                      {interests.map(interest => (
                        <Checkbox
                          key={interest}
                          id={`interest-${interest}`}
                          labelText={interest}
                          checked={formData.interests.includes(interest)}
                          onChange={(_, { checked }) => handleCheckboxChange('interests', interest, checked)}
                        />
                      ))}
                    </fieldset>
                  </Column>
                </Grid>
              </div>

              <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
                <h3
                  style={{
                    marginBottom: 'var(--cds-spacing-05)',
                    fontSize: 'var(--cds-heading-03-font-size)',
                    fontWeight: 'var(--cds-heading-03-font-weight)',
                  }}
                >
                  Demo Scheduling Preferences
                </h3>
                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <DatePicker datePickerType="single" value={formData.preferredDate}>
                      <DatePickerInput
                        id="preferredDate"
                        placeholder="mm/dd/yyyy"
                        labelText="Preferred Demo Date (Optional)"
                        value={formData.preferredDate}
                        onChange={e => handleInputChange('preferredDate', e.target.value)}
                        helperText="We'll try to accommodate your preference"
                      />
                    </DatePicker>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <TimePicker
                      id="preferredTime"
                      labelText="Preferred Demo Time (Optional)"
                      value={formData.preferredTime}
                      onChange={e => handleInputChange('preferredTime', e.target.value)}
                      helperText="All times are EST"
                    >
                      <TimePickerSelect id="demo-time-picker">
                        <SelectItem value="" text="Select time" />
                        <SelectItem value="09:00" text="9:00 AM" />
                        <SelectItem value="10:00" text="10:00 AM" />
                        <SelectItem value="11:00" text="11:00 AM" />
                        <SelectItem value="12:00" text="12:00 PM" />
                        <SelectItem value="13:00" text="1:00 PM" />
                        <SelectItem value="14:00" text="2:00 PM" />
                        <SelectItem value="15:00" text="3:00 PM" />
                        <SelectItem value="16:00" text="4:00 PM" />
                        <SelectItem value="17:00" text="5:00 PM" />
                      </TimePickerSelect>
                    </TimePicker>
                  </Column>
                </Grid>
              </div>

              <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
                <FluidTextArea
                  id="message"
                  labelText="Additional Information (Optional)"
                  placeholder="Tell us more about your specific needs, goals, or questions..."
                  rows={4}
                  value={formData.message}
                  onChange={e => handleInputChange('message', e.target.value)}
                  helperText="Help us prepare for your demo"
                />
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <Checkbox
                  id="consent"
                  labelText="I agree to be contacted by Sigma08 regarding my demo request and educational needs"
                  checked={formData.consent}
                  onChange={(_, { checked }) => handleInputChange('consent', checked)}
                  invalid={!!errors.consent}
                  invalidText={errors.consent}
                />
              </div>

              <ButtonSet>
                <Button kind="primary" renderIcon={Calendar} type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loading small withOverlay={false} />
                      Scheduling...
                    </>
                  ) : (
                    'Schedule Demo'
                  )}
                </Button>
                <Button kind="secondary" onClick={() => navigate('/calculators')}>
                  Calculate ROI First
                </Button>
              </ButtonSet>
            </Form>
          </div>
        </Column>

        <Column lg={4} md={2} sm={4}>
          <div
            style={{
              padding: 'var(--cds-spacing-09) 0 var(--cds-spacing-09) var(--cds-spacing-06)',
              position: 'sticky',
              top: 'var(--cds-spacing-06)',
            }}
          >
            <Tile
              style={{
                padding: 'var(--cds-spacing-06)',
                marginBottom: 'var(--cds-spacing-06)',
              }}
            >
              <h3
                style={{
                  marginBottom: 'var(--cds-spacing-05)',
                  fontSize: 'var(--cds-heading-03-font-size)',
                  fontWeight: 'var(--cds-heading-03-font-weight)',
                }}
              >
                What to Expect
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--cds-spacing-04)',
                  }}
                >
                  <CheckmarkFilled
                    size={20}
                    style={{
                      marginRight: 'var(--cds-spacing-03)',
                      color: 'var(--cds-support-success)',
                      flexShrink: 0,
                    }}
                  />
                  <span>30-minute personalized demonstration</span>
                </li>
                <li
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--cds-spacing-04)',
                  }}
                >
                  <CheckmarkFilled
                    size={20}
                    style={{
                      marginRight: 'var(--cds-spacing-03)',
                      color: 'var(--cds-support-success)',
                      flexShrink: 0,
                    }}
                  />
                  <span>Tailored to your institution's needs</span>
                </li>
                <li
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--cds-spacing-04)',
                  }}
                >
                  <CheckmarkFilled
                    size={20}
                    style={{
                      marginRight: 'var(--cds-spacing-03)',
                      color: 'var(--cds-support-success)',
                      flexShrink: 0,
                    }}
                  />
                  <span>Live Q&A with education specialist</span>
                </li>
                <li
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: 'var(--cds-spacing-04)',
                  }}
                >
                  <CheckmarkFilled
                    size={20}
                    style={{
                      marginRight: 'var(--cds-spacing-03)',
                      color: 'var(--cds-support-success)',
                      flexShrink: 0,
                    }}
                  />
                  <span>ROI calculation for your scenario</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CheckmarkFilled
                    size={20}
                    style={{
                      marginRight: 'var(--cds-spacing-03)',
                      color: 'var(--cds-support-success)',
                      flexShrink: 0,
                    }}
                  />
                  <span>Implementation roadmap discussion</span>
                </li>
              </ul>
            </Tile>

            <Tile
              style={{
                padding: 'var(--cds-spacing-06)',
                marginBottom: 'var(--cds-spacing-06)',
              }}
            >
              <h3
                style={{
                  marginBottom: 'var(--cds-spacing-05)',
                  fontSize: 'var(--cds-heading-03-font-size)',
                  fontWeight: 'var(--cds-heading-03-font-weight)',
                }}
              >
                Quick Stats
              </h3>
              <div style={{ marginBottom: 'var(--cds-spacing-04)' }}>
                <div
                  style={{
                    fontSize: 'var(--cds-heading-04-font-size)',
                    fontWeight: 'var(--cds-heading-04-font-weight)',
                    color: 'var(--cds-interactive-01)',
                  }}
                >
                  98%
                </div>
                <div
                  style={{
                    fontSize: 'var(--cds-body-short-01-font-size)',
                    color: 'var(--cds-text-secondary)',
                  }}
                >
                  Customer Satisfaction
                </div>
              </div>
              <div style={{ marginBottom: 'var(--cds-spacing-04)' }}>
                <div
                  style={{
                    fontSize: 'var(--cds-heading-04-font-size)',
                    fontWeight: 'var(--cds-heading-04-font-weight)',
                    color: 'var(--cds-interactive-01)',
                  }}
                >
                  250K+
                </div>
                <div
                  style={{
                    fontSize: 'var(--cds-body-short-01-font-size)',
                    color: 'var(--cds-text-secondary)',
                  }}
                >
                  Students Impacted
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 'var(--cds-heading-04-font-size)',
                    fontWeight: 'var(--cds-heading-04-font-weight)',
                    color: 'var(--cds-interactive-01)',
                  }}
                >
                  45%
                </div>
                <div
                  style={{
                    fontSize: 'var(--cds-body-short-01-font-size)',
                    color: 'var(--cds-text-secondary)',
                  }}
                >
                  Average Score Improvement
                </div>
              </div>
            </Tile>

            <div style={{ marginTop: 'var(--cds-spacing-06)' }}>
              <h4
                style={{
                  marginBottom: 'var(--cds-spacing-03)',
                  fontSize: 'var(--cds-heading-02-font-size)',
                  fontWeight: 'var(--cds-heading-02-font-weight)',
                }}
              >
                Questions?
              </h4>
              <p
                style={{
                  fontSize: 'var(--cds-body-short-01-font-size)',
                  marginBottom: 'var(--cds-spacing-04)',
                  color: 'var(--cds-text-secondary)',
                }}
              >
                Our team is ready to help
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 'var(--cds-spacing-03)',
                }}
              >
                <Phone
                  size={16}
                  style={{
                    marginRight: 'var(--cds-spacing-03)',
                    color: 'var(--cds-icon-primary)',
                  }}
                />
                <span style={{ fontSize: 'var(--cds-body-short-01-font-size)' }}>1-800-SIGMA08</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Email
                  size={16}
                  style={{
                    marginRight: 'var(--cds-spacing-03)',
                    color: 'var(--cds-icon-primary)',
                  }}
                />
                <span style={{ fontSize: 'var(--cds-body-short-01-font-size)' }}>sales@sigma08.com</span>
              </div>
            </div>
          </div>
        </Column>
      </Grid>

      {/* Success Modal */}
      <Modal
        open={showSuccess}
        onRequestClose={() => {
          setShowSuccess(false);
          navigate('/');
        }}
        modalHeading="Demo Request Received!"
        primaryButtonText="Return to Home"
        onRequestSubmit={() => navigate('/')}
      >
        <div style={{ padding: '1rem 0' }}>
          <CheckmarkFilled size={48} style={{ color: '#24a148', marginBottom: '1rem' }} />
          <h3 style={{ marginBottom: '1rem' }}>Thank you for your interest in Sigma08!</h3>
          <p style={{ marginBottom: '1rem' }}>
            We've received your demo request and an education specialist will contact you within 24 hours to schedule
            your personalized demonstration.
          </p>
          <p>
            In the meantime, feel free to explore our calculators to see the potential impact Sigma08 can have on your
            educational outcomes.
          </p>
        </div>
      </Modal>

      <MarketingFooter />
    </div>
  );
};

export default DemoRequestPage;
